// ✅ src/pages/VehicleV2/TyreMaintenanceReport_Print.jsx
// Handles both: Print All (filtered) and Print Single Row (by ID)

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/tyreMaintenanceReport";

const toStr = (v) => String(v ?? "").trim();
const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
};

export default function TyreMaintenanceReportPrint() {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Get params from URL or state
  const searchParams = new URLSearchParams(location.search);
  const stateFilters = location?.state?.filters || {};
  
  const branchId = toNum(
    searchParams.get("branchId") || stateFilters.branchId || localStorage.getItem("branchid"),
    8
  );

  const fromDate = toStr(searchParams.get("fromDate") || stateFilters.fromDate || "");
  const toDate = toStr(searchParams.get("toDate") || stateFilters.toDate || "");
  const serialId = toNum(searchParams.get("serialId") || stateFilters.serialId, 0);
  const tyreTypeId = toNum(searchParams.get("tyreTypeId") || stateFilters.tyreTypeId, 0);
  const vehicleId = toNum(searchParams.get("vehicleId") || stateFilters.vehicleId, 0);
  const storeId = toNum(searchParams.get("storeId") || stateFilters.storeId, 0);

  // ✅ For single row print
  const printRowId = toNum(searchParams.get("rowId") || stateFilters.rowId, 0);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  const params = useMemo(
    () => ({
      branchId,
      fromDate,
      toDate,
      serialId,
      tyreTypeId,
      vehicleId,
      storeId,
    }),
    [branchId, fromDate, toDate, serialId, tyreTypeId, vehicleId, storeId]
  );

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await axios.get(`${API_BASE}/list`, { params });
        let apiRows = res?.data?.rows || [];

        // ✅ If printing single row, filter by rowId
        if (printRowId) {
          apiRows = apiRows.filter((r) => 
            toNum(pick(r, ["fld_AddTyre_ID", "id", "rowId"])) === printRowId
          );
        }

        const normalized = apiRows.map((r, i) => ({
          sno: i + 1,
          vehicleNo: pick(r, ["fld_vehicle_regno", "VehicleNo"]),
          tyreSerial: pick(r, ["fld_Tyreserialno", "fld_vehicle_serialno"]),
          tyreModel: pick(r, ["fld_TyreModel", "fld_tyre_model"]),
          tyreType: pick(r, ["fld_tyretypename", "fld_tyretype"]),
          tyreMake: pick(r, ["fld_TyreMake", "fld_tyremake"]),
          tyrePosition: pick(r, ["fld_vehicletyre_positiontype"]),
          supplier: pick(r, ["fld_Purchasefrom", "fld_suppliername"]),
          amount: pick(r, ["fld_TyreAmounts", "fld_tyreamt"]),
          fittingDate: pick(r, ["fld_fittingdate"]),
          fittingKm: pick(r, ["fld_Fittingkmhr", "fld_fittingKM"]),
        }));

        if (!alive) return;
        setRows(normalized);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [params, printRowId]);

  const reportTitle = printRowId 
    ? "Tyre Maintenance Report - Single Record" 
    : "Tyre Maintenance Report - All Records";

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      <style>{`
        @media print {
          body { margin: 0; padding: 10px; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
        }
        @page { size: A4 landscape; margin: 0.3in; }
      `}</style>

      <div className="p-2 print:p-1">
        {/* Top actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
          >
            🖨️ Print
          </button>
        </div>

        {/* Header */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
              </div>
              <div className="text-xs">
                Phone No : 9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <h2 className="text-base font-bold">{reportTitle}</h2>
        </div>

        {/* Filter Info */}
        <div className="text-xs border border-black px-2 py-1 mb-2">
          <b>Branch ID:</b> {branchId} &nbsp;&nbsp;
          {fromDate && <><b>From:</b> {fromDate} &nbsp;&nbsp;</>}
          {toDate && <><b>To:</b> {toDate} &nbsp;&nbsp;</>}
          {serialId > 0 && <><b>Serial ID:</b> {serialId} &nbsp;&nbsp;</>}
          {tyreTypeId > 0 && <><b>Tyre Type ID:</b> {tyreTypeId} &nbsp;&nbsp;</>}
          {vehicleId > 0 && <><b>Vehicle ID:</b> {vehicleId} &nbsp;&nbsp;</>}
          {storeId > 0 && <><b>Store ID:</b> {storeId}</>}
          {printRowId > 0 && <><b>Row ID:</b> {printRowId}</>}
        </div>

        {/* Loading / Error */}
        {loading ? (
          <div className="no-print p-3 border rounded bg-gray-50 text-sm mb-2">Loading report...</div>
        ) : err ? (
          <div className="no-print p-3 border rounded bg-red-50 text-sm text-red-700 mb-2">{err}</div>
        ) : null}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 font-bold text-center">S.No</th>
                <th className="border border-black px-1 py-1 font-bold text-left">Vehicle No</th>
                <th className="border border-black px-1 py-1 font-bold text-left">Tyre Serial No</th>
                <th className="border border-black px-1 py-1 font-bold text-left">Tyre Model</th>
                <th className="border border-black px-1 py-1 font-bold text-left">Tyre Type</th>
                <th className="border border-black px-1 py-1 font-bold text-left">Tyre Make</th>
                <th className="border border-black px-1 py-1 font-bold text-left">Tyre Position</th>
                <th className="border border-black px-1 py-1 font-bold text-left">Supplier</th>
                <th className="border border-black px-1 py-1 font-bold text-right">Amount</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Fitting Date</th>
                <th className="border border-black px-1 py-1 font-bold text-right">Fitting KM/HR</th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="border border-black px-2 py-2 text-center">
                    No records found for selected filters.
                  </td>
                </tr>
              ) : (
                rows.map((x, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="border border-black px-1 py-1 text-center">{x.sno}</td>
                    <td className="border border-black px-1 py-1">{x.vehicleNo}</td>
                    <td className="border border-black px-1 py-1">{x.tyreSerial}</td>
                    <td className="border border-black px-1 py-1">{x.tyreModel}</td>
                    <td className="border border-black px-1 py-1">{x.tyreType}</td>
                    <td className="border border-black px-1 py-1">{x.tyreMake}</td>
                    <td className="border border-black px-1 py-1">{x.tyrePosition}</td>
                    <td className="border border-black px-1 py-1">{x.supplier}</td>
                    <td className="border border-black px-1 py-1 text-right">
                      {x.amount ? `₹${parseFloat(x.amount).toFixed(2)}` : "-"}
                    </td>
                    <td className="border border-black px-1 py-1 text-center">{x.fittingDate}</td>
                    <td className="border border-black px-1 py-1 text-right">{x.fittingKm}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>
            Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
          </p>
          <p className="mt-1">
            <b>Total Records:</b> {rows.length}
          </p>
        </div>
      </div>
    </div>
  );
}